// React imports
import { FC, useMemo } from "react";

// Internal app imports
import { ACCOUNT_DETAILS_CREATE_NEW_LEAD_MODAL_ID } from "@/modules/pipeline/account-details/modals";
import { useAccountDetailsContext } from "@/modules/pipeline/account-details/context";
import { AccountDetailsLeadItem } from "@/components/modules/pipeline/account-details/sections/leads-section/lead-item";
import { ACCOUNT_SIDEBAR_ACTION_BUTTON_CLASSNAME } from "@/modules/pipeline/account-details/constants";

// Shared lib imports
import CenteredSpinner from "shared/ui/spinners/centered-spinner";
import { modalHelpers } from "shared/lib/helpers/modalHelpers";
import { ContactDetailI } from "shared/lib/interfaces/account";
import { PropsWithClassNameI } from "shared/lib/interfaces/ui";
import { AccountDetailsSectionContainer } from "shared/ui/account-details/component-wrapper";
import { DataStatesWrapper } from "shared/ui/data-states-wrapper";

const openAddLeadModal = () =>
  modalHelpers.open(ACCOUNT_DETAILS_CREATE_NEW_LEAD_MODAL_ID);

const Header = ({ title = "Leads" }: { title?: string }) => {
  return (
    <div className="flex w-full items-center justify-between">
      <h6>{title}</h6>

      <button
        className={ACCOUNT_SIDEBAR_ACTION_BUTTON_CLASSNAME}
        onClick={openAddLeadModal}
      >
        Add
      </button>
    </div>
  );
};

export const LeadsSection: FC<
  PropsWithClassNameI & {
    title?: string;
    filterFunc?: (contact: ContactDetailI) => boolean;
  }
> = ({ className, filterFunc, title }) => {
  const { contacts: allContacts, accountDetailsApi } =
    useAccountDetailsContext();

  const contacts = useMemo(() => {
    if (!filterFunc) return allContacts;

    return allContacts?.filter(filterFunc);
  }, [allContacts, filterFunc]);

  return (
    <AccountDetailsSectionContainer
      title={<Header title={title} />}
      className={className}
      contentClassName="py-0"
    >
      <DataStatesWrapper
        api={accountDetailsApi}
        loading={<CenteredSpinner className="h-[160px]" />}
      >
        {contacts && (
          <div className="flex flex-col divide-y">
            {contacts.map((contact) => (
              <AccountDetailsLeadItem
                key={contact.id}
                contact={contact}
                className="lg:min-w-none py-3"
              />
            ))}
          </div>
        )}
      </DataStatesWrapper>
    </AccountDetailsSectionContainer>
  );
};
